import { useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useProctorGroups } from '@indeed/frontend-proctor-utils';
import { Flow, FlowStep, SubFlow } from '../types';
import GettextFactory from '../../../util/GettextFactory';
import { useGetAutoApplyDraftListQuery } from '../../../pages/auto-apply/__generated__/AutoApplyIndexPage.generated';
import { getDraftIdsNeedingQuestions } from '../../../pages/auto-apply/unfilledQuestions';

// =============================================================================
// INACTIVE STUB
// =============================================================================

/** Stub flow returned when this hook is not active */
const INACTIVE_STUB: {
    flow: Flow;
    subFlows: SubFlow[];
    getNextStep: (currentRoute: string) => Promise<string | null>;
    draftIds: string[];
    loading: boolean;
} = {
    flow: {
        id: 'auto-apply',
        name: 'Auto Apply Flow',
        description: 'Review and submit multiple job applications',
        steps: [],
    },
    subFlows: [],
    getNextStep: () => Promise.resolve(null),
    draftIds: [],
    loading: false,
};

// =============================================================================
// FLOW HOOK
// =============================================================================

/**
 * Hook to get the auto-apply flow configuration.
 *
 * This flow is unique because:
 * 1. Draft IDs are read from the URL query parameter (`?draftIds=draft1,draft2,draft3`)
 * 2. Steps are per-draft (dynamic based on draftId from URL)
 * 3. A draft whose `unfilledScreenerQuestionCount` is > 0 gets a /questions step ahead of its review
 * 4. Sub-pages (/contact-info, /resume, /questions, with /edit suffix) return to review
 * 5. From review, getNextStep advances to the next draft's questions or review page
 * 6. After all drafts reviewed, getNextStep returns the confirmation page
 *
 * @param isActive - Whether this flow is currently active. If false, returns a minimal stub.
 * @returns Object containing flow definition, getNextStep function, and draft IDs
 */
export const useAutoApplyFlow = (
    isActive: boolean = true,
): {
    flow: Flow;
    subFlows: SubFlow[];
    getNextStep: (currentRoute: string) => Promise<string | null>;
    /** All draft IDs from the URL query parameter */
    draftIds: string[];
    loading: boolean;
} => {
    const { draftId } = useParams<{ draftId: string }>();
    const [searchParams] = useSearchParams();
    const gt = GettextFactory();
    const { getGroup } = useProctorGroups();
    const draftIdsParam = searchParams.get('draftIds');

    // Progressive-automation banner entry (rnhp_auto_apply_banner_tst value 2).
    // In this bucket the confirmation page opens each draft's review as a
    // dedicated `/review/edit` detour with a single "Update" CTA, so the review
    // sub-flow below is only registered for that bucket. Outside value 2 the
    // review page keeps its Previous/Next main-spine behavior.
    const isProgressiveAutomation = getGroup('rnhp_auto_apply_banner_tst')?.value === 2;

    // Extract draft IDs from URL query parameter (?draftIds=draft1,draft2,draft3)
    const draftIds = useMemo(() => {
        if (!isActive) return INACTIVE_STUB.draftIds;
        if (!draftIdsParam) return [];
        return draftIdsParam.split(',').filter(Boolean);
    }, [draftIdsParam, isActive]);

    // The screener-questions step is driven by the draft's unfilled-question count. This is the
    // same query AutoApplyIndexPage runs, so Apollo serves it from cache on every later route.
    const { data: draftListData, loading: draftListLoading } = useGetAutoApplyDraftListQuery({
        fetchPolicy: 'cache-first',
        skip: !isActive,
    });

    /**
     * Draft IDs with at least one screener question still to answer. A `null` count means the
     * server doesn't know, which is distinct from `0` — neither adds the step.
     *
     * While the query is in flight this is empty, so consumers must respect `loading` before
     * stepping the spine (see ReviewPage's Next). If the query *fails* it stays empty and the
     * step is skipped: the same fail-open stance AutoApplyIndexPage takes, so a broken draft-list
     * call degrades to the old no-questions flow rather than stranding the seeker.
     */
    const draftIdsNeedingQuestions = useMemo(
        () => getDraftIdsNeedingQuestions(draftListData),
        [draftListData],
    );

    const flow: Flow = useMemo(() => {
        if (!isActive) {
            return INACTIVE_STUB.flow;
        }
        // Main progression: per draft, an optional questions step then its review →
        // confirmation → success. The `/edit` pages (contact-info, resume, questions)
        // remain standalone side-trips that return to the review via getNextStep.
        const steps: FlowStep[] = draftIds.flatMap((id) => [
            ...(draftIdsNeedingQuestions.has(id)
                ? [
                      {
                          routes: [`/auto-apply/${id}/questions`],
                          label: gt.gettext('Questions'),
                          skipOnBack: false,
                      },
                  ]
                : []),
            {
                routes: [`/auto-apply/${id}/review`],
                label: gt.gettext('Review'),
                skipOnBack: false,
            },
        ]);

        // Add confirmation and success as final steps
        steps.push(
            {
                routes: ['/auto-apply/confirmation'],
                label: gt.gettext('Confirmation'),
                skipOnBack: false,
            },
            {
                routes: ['/auto-apply/success'],
                label: gt.gettext('Success'),
                skipOnBack: true,
                canGoBack: false,
            },
        );

        return {
            id: 'auto-apply',
            name: 'Auto Apply Flow',
            description: 'Review and submit multiple job applications',
            steps,
        };
    }, [draftIds, draftIdsNeedingQuestions, gt, isActive]);

    // Review detour opened from the confirmation page in the progressive-
    // automation bucket. Clicking a job application enters `/review/edit` as a
    // side-trip (see enterSubFlow in usePageFlow); the review page then renders a
    // single "Update" CTA that calls exitSubFlow to return to the confirmation
    // page. Registered here (not on the shared Flow type) so useCurrentFlow's
    // findSubFlow/findSubFlowIndex classify `/review/edit` as a sub-flow step
    // purely from the route. Only registered for value 2 — outside that bucket
    // the review page stays on the main spine with Previous/Next.
    const subFlows = useMemo<SubFlow[]>(() => {
        if (!isProgressiveAutomation) return [];
        return [
            {
                steps: [
                    {
                        routes: ['/auto-apply/:draftId/review/edit'],
                        label: gt.gettext('Review'),
                    },
                ],
                defaultReturnRoute: '/auto-apply/confirmation',
            },
        ];
    }, [isProgressiveAutomation, gt]);

    /**
     * Dynamic getNextStep for auto-apply flow.
     *
     * Navigation logic:
     * 1. From /review/edit (review detour) → no next step; the page exits the
     *    sub-flow via exitSubFlow('/auto-apply/confirmation')
     * 2. From /contact-info, /resume, /questions (with or without /edit) → return to current draft's review
     * 3. From /review → next draft's questions (if it has unanswered ones) or review, otherwise confirmation
     * 4. From /confirmation → success
     */
    const getNextStep = useCallback(
        (currentRoute: string): Promise<string | null> => {
            // Review detour (`/review/edit`) is a single-step sub-flow: there is no
            // "next" step — the page leaves via exitSubFlow — so resolve null and
            // avoid falling through to the `/contact-info`/`/resume` handling below.
            if (currentRoute.endsWith('/review/edit')) {
                return Promise.resolve(null);
            }

            // From any contact-info/resume/questions page (with or without /edit), return to review
            if (
                draftId &&
                (currentRoute.includes('/contact-info') ||
                    currentRoute.includes('/resume') ||
                    currentRoute.includes('/questions'))
            ) {
                return Promise.resolve(`/auto-apply/${draftId}/review`);
            }

            // From review page → next draft's review or confirmation
            if (draftId && currentRoute.endsWith('/review')) {
                const currentIndex = draftIds.indexOf(draftId);

                if (currentIndex >= 0 && currentIndex < draftIds.length - 1) {
                    // There's a next draft — answer its outstanding questions before its review
                    const nextDraftId = draftIds[currentIndex + 1];
                    return Promise.resolve(
                        draftIdsNeedingQuestions.has(nextDraftId)
                            ? `/auto-apply/${nextDraftId}/questions`
                            : `/auto-apply/${nextDraftId}/review`,
                    );
                }

                // No more drafts — go to confirmation
                return Promise.resolve('/auto-apply/confirmation');
            }

            // From confirmation, go to success
            if (currentRoute === '/auto-apply/confirmation') {
                return Promise.resolve('/auto-apply/success');
            }

            // Fallback: find current step and return next
            const currentStepIndex = flow.steps.findIndex((step) =>
                step.routes.includes(currentRoute),
            );

            if (currentStepIndex === -1 || currentStepIndex === flow.steps.length - 1) {
                return Promise.resolve(null);
            }

            return Promise.resolve(flow.steps[currentStepIndex + 1].routes[0]);
        },
        [flow.steps, draftId, draftIds, draftIdsNeedingQuestions],
    );

    if (!isActive) {
        return INACTIVE_STUB;
    }

    return {
        flow,
        // Per-draft edit pages (contact-info/resume/questions) use replace
        // navigation, not detours. The only sub-flow is the progressive-automation
        // review detour (`/review/edit`), registered above for value 2.
        subFlows,
        getNextStep,
        draftIds,
        // The step list can't be finalised until the unfilled-question counts land.
        loading: draftListLoading,
    };
};
